import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import ScrollToTop from "./components/ScrollToTop";
import { logPageView } from "./utils/analytics";

// Lazy load almost everything to break complex dependency chains
const Home = lazy(() => import("./pages/Home"));
const Admin = lazy(() => import("./pages/Admin"));
const Login = lazy(() => import("./pages/Login"));
const AIAssistant = lazy(() => import("./components/AIAssistant"));
const NewsTicker = lazy(() => import("./components/NewsTicker"));
const DigitalCircuits = lazy(() => import("./components/DigitalCircuits"));
const WishlistDrawer = lazy(() => import("./components/WishlistDrawer"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
    </div>
  );
}

function MainLayout({ children }) {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-red-500/30 flex flex-col relative overflow-hidden">
      <div className="scanline-overlay" />
      <Suspense fallback={null}>
        <DigitalCircuits />
      </Suspense>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#000000",
            color: "#ffffff",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(12px)",
          },
        }}
      />

      <div className="relative z-10 flex flex-col min-h-screen">
        {!isLoginPage && (
          <>
            <Suspense fallback={<div className="h-10 bg-black/50" />}>
              <NewsTicker />
            </Suspense>
            <Navbar />
          </>
        )}
        <CartDrawer />
        <Suspense fallback={null}>
          <WishlistDrawer />
        </Suspense>
        <Suspense fallback={null}>
          <AIAssistant />
        </Suspense>
        <main
          className={`container mx-auto px-4 ${
            isLoginPage ? "py-8" : "pt-[120px] pb-8"
          } max-w-7xl flex-grow relative`}
        >
          {children}
        </main>
        {!isLoginPage && <Footer />}
      </div>
    </div>
  );
}

function App() {
  const location = useLocation();

  // Rastrear cambios de página
  useEffect(() => {
    logPageView(location.pathname, document.title);
  }, [location]);

  return (
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              </Suspense>
            }
          />
        </Routes>
      </Suspense>
      <ScrollToTop />
    </MainLayout>
  );
}

export default App;
